import type { ReactNode } from 'react';
import { PageMeta } from '../components/PageMeta';
import {
  LEGACY_PUBLIC_DOCUMENTS,
  type LegacyContentBlock,
  type LegacyPublicPath,
} from '../content/legacy-public-content';
import { Band, Btn, Eyebrow } from '../site/primitives';

function linkedText(text: string): ReactNode[] {
  return text.split(/((?:https?:\/\/|www\.)[^\s]+|[\w.+-]+@[\w.-]+\.[A-Za-z]{2,})/g).map((part, index) => {
    if (/^[\w.+-]+@[\w.-]+\.[A-Za-z]{2,}$/.test(part)) {
      return <a key={`${part}-${index}`} href={`mailto:${part.toLowerCase()}`}>{part}</a>;
    }
    if (/^(?:https?:\/\/|www\.)/.test(part)) {
      const href = part.startsWith('http') ? part : `https://${part}`;
      return <a key={`${part}-${index}`} href={href}>{part}</a>;
    }
    return part;
  });
}

function DocumentBlock({ block }: { block: LegacyContentBlock }) {
  if (block.kind === 'heading') return <h2>{block.text}</h2>;
  if (block.kind === 'subheading') return <h3>{block.text}</h3>;
  if (block.kind === 'unordered-list' || block.kind === 'ordered-list') {
    const List = block.kind === 'ordered-list' ? 'ol' : 'ul';
    return <List>{block.items.map((item) => <li key={item}>{linkedText(item)}</li>)}</List>;
  }
  return <p>{linkedText(block.text)}</p>;
}

export function PublicDocumentsPage({ path }: { path: LegacyPublicPath }) {
  const document = LEGACY_PUBLIC_DOCUMENTS[path];
  const isPolicy = path !== '/learn-more';
  return (
    <>
      <PageMeta title={document.title} description={document.description} />
      <Band className="public-document-hero" space="compact" width="read" labelledBy="public-document-title">
        <div className="u-stack u-stack--lg">
          <Eyebrow pill>{isPolicy ? 'Published policy' : 'Customer guide'}</Eyebrow>
          <h1 id="public-document-title" className="u-h1">{document.title}</h1>
          <p className="u-lead">{document.description}</p>
          {document.effectiveDate ? (
            <dl className="public-document-dates" aria-label="Publication dates">
              <div><dt>Effective date</dt><dd>{document.effectiveDate}</dd></div>
              <div><dt>Last updated</dt><dd>{document.lastUpdated}</dd></div>
            </dl>
          ) : null}
          {document.download ? (
            <div className="u-cluster">
              <Btn href={document.download.href}>{document.download.label}</Btn>
              <span className="u-small">Approved source document · PDF</span>
            </div>
          ) : null}
        </div>
      </Band>

      {document.download ? null : (
        <Band className="public-document-band" width="read" flushTop>
          <article className="u-prose public-document-copy">
            {document.blocks.map((block, index) => <DocumentBlock key={`${block.kind}-${index}`} block={block} />)}
          </article>
        </Band>
      )}
    </>
  );
}
